import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The phone's back button, on a form with unsaved changes (Brad, 2026-09-23: "If I hit
 * back too many times and am editing a form, it needs to ask if I want to save before
 * exiting the form. I had it filled out and it lost all my changes with one back push.")
 *
 * While `dirty`, one extra history entry sits on top of the page. A back press pops it
 * instead of leaving; we catch that, put it back, and report `blocked` so the form can
 * show "Save" / "Leave without saving". `allowLeave()` drops the guard and completes the
 * back the user asked for. Closing the tab or reloading gets the browser's own prompt.
 *
 * Plays with ZoomImage's own entry: the enlarged picture pushes on top of the guard, so
 * back closes the picture first and only the next back reaches the form.
 */
export default function useBackGuard(dirty: boolean) {
  const [blocked, setBlocked] = useState(false);
  const armed = useRef(false);
  const leaving = useRef(false);

  useEffect(() => {
    if (!dirty) {
      // Clean again (saved, or cancelled): drop our entry if it is on top.
      if (armed.current && window.history.state && window.history.state.backGuard) {
        armed.current = false;
        window.history.back();
      }
      armed.current = false;
      setBlocked(false);
      return undefined;
    }
    if (!armed.current) {
      window.history.pushState({ backGuard: true }, '');
      armed.current = true;
    }
    const onPop = () => {
      if (leaving.current) return;
      // Our guard entry was popped: put it back and ask.
      window.history.pushState({ backGuard: true }, '');
      setBlocked(true);
    };
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('popstate', onPop);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [dirty]);

  // "Leave without saving": drop the guard and go back the way they asked.
  const allowLeave = useCallback(() => {
    leaving.current = true;
    armed.current = false;
    setBlocked(false);
    window.history.go(-2);
  }, []);

  const stay = useCallback(() => setBlocked(false), []);

  return { blocked, allowLeave, stay };
}
